import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

const SAVE_KEY_PREFIX = 'arcade:take-me-home:save:'

function clearSaveData() {
  try {
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key && key.startsWith(SAVE_KEY_PREFIX)) doomed.push(key)
    }
    doomed.forEach((key) => localStorage.removeItem(key))
  } catch {
    /* storage unavailable — reload alone is the best we can do */
  }
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Take Me Home crashed:', error, info.componentStack)
  }

  private reload = () => {
    window.location.reload()
  }

  private resetAndReload = () => {
    clearSaveData()
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          background: 'radial-gradient(circle at 50% 30%, #1d2030, #101220 80%)',
          color: '#f2ecdf',
          fontFamily: "'Nunito', system-ui, sans-serif",
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 360 }}>
          <div
            style={{
              margin: '0 auto 16px',
              height: 56,
              width: 56,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #ffe6b8, #ffb454 60%)',
              boxShadow: '0 0 24px #ffb45466',
            }}
          />
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>The little light flickered out</h1>
          <p style={{ fontSize: 14, opacity: 0.75, marginBottom: 20 }}>
            Something went wrong while loading the game. Reloading usually fixes it. If the crash keeps happening,
            your local save data may be damaged — you can clear it and start fresh.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={this.reload}
              style={{
                padding: '12px 16px',
                borderRadius: 14,
                border: 'none',
                background: '#ffb454',
                color: '#231a0d',
                fontWeight: 800,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              Reload the game
            </button>
            <button
              type="button"
              onClick={this.resetAndReload}
              style={{
                padding: '12px 16px',
                borderRadius: 14,
                border: '1px solid #f2ecdf33',
                background: 'transparent',
                color: '#f2ecdf',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Clear save data and reload
            </button>
          </div>
        </div>
      </div>
    )
  }
}
